import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useGenericFieldsData } from 'containers/GenericFieldsHandlingLayer/context';
import type { IWholeSchemaWithState } from 'containers/GenericFieldsHandlingLayer/types';
import type { BrandModule } from 'common/types/brandModule';
import Loader from 'common/components/Loader';
import ErrorMessage from 'common/components/ErrorMessage';
import { setCommitAction } from 'common/constants';
import { CommitAction } from 'utils/commit-action';
import useSearchQuery from 'utils/useSearchQuery';
import { BRANDS, BrandKey, resolveBrandKey } from './brands';
import { readBrandHint, writeBrandHint } from './brandHint';

const PROJECT_KEY = 'variables.project';
const COMMIT_ACTION_KEY = 'variables.commitAction';
const DEFAULT_SCHEMA_ERROR = 'Failed to load the configuration, please reload the page';

type Status =
  | { kind: 'loading' }
  | { kind: 'ready', brand: BrandModule }
  | { kind: 'error', message: string };

type Prefetch = { key: BrandKey, promise: Promise<{ default: BrandModule }> };

const latestServerI18n = (queryClient: ReturnType<typeof useQueryClient>): Record<string, unknown> | undefined => {
  const entries = queryClient.getQueriesData<IWholeSchemaWithState>(['whole-schema']);
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const data = entries[i][1];
    if (data && data.i18n) {
      return data.i18n;
    }
  }
  return undefined;
};

const isCommitAction = (value: unknown): value is CommitAction => (
  typeof value === 'string' && (Object.values(CommitAction) as Array<string>).includes(value)
);

const BrandSwitcher = () => {
  const { i18n } = useTranslation();
  const { isLoading, error } = useGenericFieldsData();
  const queryClient = useQueryClient();
  const searchParams = useSearchQuery();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const startedRef = useRef(false);
  const prefetchRef = useRef<Prefetch>();

  useEffect(() => {
    const hint = readBrandHint();
    if (hint) {
      prefetchRef.current = { key: hint, promise: BRANDS[hint].load() };
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    const projectId = i18n.getResource('en', 'translation', PROJECT_KEY);
    const settled = projectId !== undefined || !isLoading || !!error;
    if (!settled) {
      return;
    }
    startedRef.current = true;

    if (projectId === undefined && error) {
      setStatus({ kind: 'error', message: error.data?.message || error.statusText || DEFAULT_SCHEMA_ERROR });
      return;
    }

    const resolution = resolveBrandKey(projectId, process.env.REACT_APP_DEFAULT_PROJECT);
    if (!resolution.ok) {
      setStatus({ kind: 'error', message: resolution.reason });
      return;
    }

    const { key } = resolution;
    const prefetched = prefetchRef.current;
    const pending = prefetched && prefetched.key === key ? prefetched.promise : BRANDS[key].load();

    pending
      .then(({ default: brand }) => {
        i18n.addResourceBundle('en', 'translation', brand.i18n, true, true);
        const serverI18n = latestServerI18n(queryClient);
        if (serverI18n) {
          i18n.addResourceBundle('en', 'translation', serverI18n, true, true);
        }
        const commitAction = i18n.getResource('en', 'translation', COMMIT_ACTION_KEY);
        if (isCommitAction(commitAction)) {
          setCommitAction(commitAction);
        }
        writeBrandHint(key);
        setStatus({ kind: 'ready', brand });
        return i18n.changeLanguage('en');
      })
      .catch(() => {
        setStatus({ kind: 'error', message: `Failed to load the ${key} frontend, please reload the page` });
      });
  });

  if (status.kind === 'ready') {
    const View = 'printId' in searchParams ? status.brand.PrintView : status.brand.FormSwitcher;
    return <View />;
  }
  if (status.kind === 'error') {
    return <ErrorMessage text={status.message} />;
  }
  return <Loader />;
};

export default BrandSwitcher;
